/**
 * Post Data to an endpoint via fetch.
 * @param {string} url The URL to send data to.
 * @param {object} data An objec tof data to send.
 * @returns {mixed} Returned post data.
 */
export const postData = async (url, data = {}, token = '') => {
	let headers = {
		'Content-Type': 'application/json',
	};

	if (token) {
		headers = {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`,
		};
	}

	const response = await fetch(url, {
		method: 'POST',
		mode: 'cors',
		cache: 'no-cache',
		credentials: 'same-origin',
		headers: headers,
		redirect: 'follow',
		referrerPolicy: 'no-referrer',
		body: JSON.stringify(data),
	});

	return response.json();
};

/**
 * Run a get request via fetch.
 * @param {string} url The URL to query.
 * @returns {mixed} Fetch data.
 */
export const fetchData = async (url) => {
	const response = await fetch(url, {
		method: 'GET',
		mode: 'cors',
		cache: 'no-cache',
		credentials: 'same-origin',
		redirect: 'follow',
		referrerPolicy: 'no-referrer',
	});

	return response.json();
};

/**
 * Grab dynamic classes from modules.
 * @param {array} style The style array.
 * @param {array} customClass The dynamic array of classes to get from style.
 * @returns {string} space seperated string of classes.
 */
export const constructLocalClasses = (style, customClass = []) => {
	// Add the default component to the array.
	let newClassList = [style.component];

	// If the class exists in style, add the next constructed class.
	customClass.forEach((cc) => {
		if (style[cc]) {
			newClassList.push(style[cc]);
		} else {
			newClassList.push(cc);
		}
	});

	return newClassList.join(' ');
};

/**
 * Takes in data & orders it A-Z
 */
export const alphabetizeData = (data) => {
	return data
		.sort((a, b) => a.title.localeCompare(b.title))
		.reduce((r, e) => {
			const key = e.title[0];
			if (!r[key]) r[key] = [];
			r[key].push(e);
			return r;
		}, {});
};

/**
 * Get the query params from the current url.
 * @param {string} providedUrl The URL to query, defaults to window.location.search.
 * @returns {array|string} Query params as an array.
 */
export const getQueryParams = (providedUrl = false) => {
	const url = providedUrl ? new URL(providedUrl) : window.location;
	const params = new URLSearchParams(url.search);
	let returnObject = {};

	for (let item of params) {
		returnObject[item[0]] = item[1];
	}

	return returnObject;
};

/// block helpers
/**
 * Strip html from string.
 * @param {string} html String of html to check.
 * @returns
 */
export function strip(html) {
	try {
		var doc = document.implementation.createDocument('http://www.w3.org/1999/xhtml', 'html', null);
		doc.documentElement.innerHTML = html;
		return doc.documentElement.textContent || doc.documentElement.innerText;
	} catch (e) {
		return '';
	}
}

/**
 * Normalise colours to rgb so we can use them with lower opacity as rgba.
 */
 export function normaliseColor(color) {
	if (!color || typeof color !== 'string') {
		return null;
	}

	const value = color.trim().toLowerCase();

	// HEX
	if (value.startsWith('#')) {
		let hex = value.slice(1);

		// #rgb / #rgba -> #rrggbb / #rrggbbaa
		if (hex.length === 3 || hex.length === 4) {
			hex = hex
				.split('')
				.map((char) => char + char)
				.join('');
		}

		const number = parseInt(hex.slice(0, 6), 16);

		return {
			r: (number >> 16) & 255,
			g: (number >> 8) & 255,
			b: number & 255,
		};
	}

	// RGB / RGBA
	if (value.startsWith('rgb')) {
		const [r, g, b] = value.match(/[\d.]+/g).map(Number);

		return { r, g, b };
	}

	// HSL / HSLA
	if (value.startsWith('hsl')) {
		const [h, s, l] = value.match(/[\d.]+/g).map(Number);

		return hslToRgb(h, s, l);
	}

	throw new Error(`Unsupported color format: ${color}`);
}

function hslToRgb(h, s, l) {
	s /= 100;
	l /= 100;

	const k = (n) => (n + h / 30) % 12;
	const a = s * Math.min(l, 1 - l);

	const f = (n) =>
		l - a * Math.max(
			-1,
			Math.min(k(n) - 3, Math.min(9 - k(n), 1))
		);

	return {
		r: Math.round(255 * f(0)),
		g: Math.round(255 * f(8)),
		b: Math.round(255 * f(4)),
	};
}


/**
 * Layout classes.
 *
 * flexWrap : wrap
 * justifyContent : center
 * orientation : vertical
 * type : flex
 * verticalAlignment : center
 */
export function layoutClass(layout = '', isConstrain = true, blockName = '') {
	let classes = '';

	// Gutenberg does not save the default Buttons layout until it is changed.
	// core/buttons defaults to flex + wrap.
	if (!layout && blockName === 'core/buttons') {
		layout = {
			type: 'flex',
			flexWrap: 'wrap',
		};
	}

	let direction = layout.justifyContent;

	if ('left' === direction) {
		direction = 'flex-start';
	}

	if ('right' === direction) {
		direction = 'flex-end';
	}

	if (!layout || Object.keys(layout).length === 0) {
		return classes;
	}

	if (layout.type && isConstrain) {
		classes += `is-layout-${layout.type} `; // previously is-{layout.type}.
	}

	if (layout.flexWrap && 'wrap' === layout.flexWrap) {
		classes += 'is-flex-wrap ';
	}

	if ('vertical' === layout.orientation && !layout.justifyContent) {
		classes += ` is-flex-direction-column is-align-items-flex-start`;
	}

	if (layout.justifyContent && 'vertical' === layout.orientation) {
		classes += ` is-align-items-${direction}`;
		classes += ` is-flex-direction-column`;
	}

	if (layout.justifyContent && 'vertical' !== layout.orientation) {
		classes += ` is-justify-content-${direction}`;
	}

	if (layout.verticalAlignment) {
		classes += ` is-align-items-${layout.verticalAlignment}`;
	}

	return classes.trim();
}

/**
 *
 */
export function alignClass(align = '', isText = true) {
	if ('' === align) {
		return '';
	}

	if (isText) {
		return ` has-text-align-${align}`;
	}

	return ` align${align}`;
}

/**
 * Returns a colour class string based on type.
 *
 * @param string colour: The colour name you want to output.
 * @param string type: What do you want the colour to be applied to.
 */
export function colourClass(colour = undefined, type = 'bg') {
	if (undefined === colour || !colour) {
		return '';
	}

	const presetPrefix = 'var:preset|color|';

	if (colour.startsWith(presetPrefix)) {
		colour = colour.replace(presetPrefix, '');
	}

	if ('text' === type) {
		return ` has-${colour}-color`;
	}

	if ('border' === type) {
		return ` has-${colour}-border-color`;
	}

	if ('bg' === type) {
		return ` has-${colour}-background-color`;
	}

	if ('gradient' === type) {
		return ` has-custom-background-color`;
	}

	return ` has-${colour}-gradient-background`;
}

/**
 * Get border styles.
 */
export const getBorderStyle = (style) => {
	let borderStyle = {};

	if (style?.width) {
		borderStyle.borderWidth = style.width;
	}

	if (style?.color) {
		borderStyle.borderColor = style.color;
		borderStyle.borderStyle = 'solid';
	}

	if (style?.style) {
		borderStyle.borderStyle = style.style;
	}

	if (style?.radius && 'object' !== typeof style?.radius) {
		borderStyle.borderRadius = style.radius;

		borderStyle['--original-border-radius'] = style.radius;
		return borderStyle;
	}

	if (style.radius && 'object' === typeof style.radius) {
		// let radiusValues = [];
		// for (const [key, value] of Object.entries(style.radius)) {
		// 	borderStyle[`border${key.charAt(0).toUpperCase() + key.slice(1)}Radius`] = value;
		// 	radiusValues.push(value);
		// }

		// borderStyle['--original-border-radius'] = radiusValues.join(' ');

		const { topLeft, topRight, bottomRight, bottomLeft } = style.radius;

		// Apply individual corners (React style)
		if (topLeft) borderStyle.borderTopLeftRadius = topLeft;
		if (topRight) borderStyle.borderTopRightRadius = topRight;
		if (bottomRight) borderStyle.borderBottomRightRadius = bottomRight;
		if (bottomLeft) borderStyle.borderBottomLeftRadius = bottomLeft;

		// 👇 Correct order array
		const values = [topLeft, topRight, bottomRight, bottomLeft];

		// 👇 Compress to shortest CSS shorthand
		const unique = [...new Set(values)];

		let shorthand;

		if (unique.length === 1) {
			shorthand = values[0]; // 20px
		} else if (values[0] === values[2] && values[1] === values[3]) {
			shorthand = `${values[0]} ${values[1]}`; // 20px 10px
		} else if (values[1] === values[3]) {
			shorthand = `${values[0]} ${values[1]} ${values[2]}`; // 20px 10px 5px
		} else {
			shorthand = values.join(' '); // full 4 values
		}

		borderStyle['--original-border-radius'] = shorthand;
	}

	return borderStyle;
};

function isOnlyDigits(str) {
  return /^\d+$/.test(str);
}

export const getSpacingStyle = (spacing, type = 'margin') => {
	let spacingStyle = {};

	if (undefined === spacing?.[type]) {
		return spacingStyle;
	}

	if ('blockGap' === type) {
		if ('object' !== typeof spacing?.[type]) {
			return {
				gap: spacing[type],
			};
		}
	}

	if (spacing?.[type] && 'object' !== typeof spacing?.[type]) {
		return {
			margin: isOnlyDigits(spacing[type]) ? `${spacing[type]}px` : spacing[type],
		};
	}

	if ('object' === typeof spacing?.[type]) {
		for (const [key, value] of Object.entries(spacing[type])) {
			if ('blockGap' !== type) {
				spacingStyle[`${[type]}${key.charAt(0).toUpperCase() + key.slice(1)}`] = isOnlyDigits(value ) ? `${value}px` : value;
			} else {
				if ('left' === key) {
					spacingStyle['columnGap'] = isOnlyDigits(value ) ? `${value}px` : value;
				} else {
					spacingStyle['rowGap'] = isOnlyDigits(value ) ? `${value}px` : value;
				}
			}
		}
	}

	return spacingStyle;
};

export const getBlockStyles = (style) => {
	const margins = style?.spacing ? getSpacingStyle(style?.spacing) : '';
	const padding = style?.spacing ? getSpacingStyle(style?.spacing, 'padding') : '';
	const gap = style?.spacing ? getSpacingStyle(style?.spacing, 'blockGap') : '';
	const border = style?.border ? getBorderStyle(style?.border) : '';

	return {
		fontSize: style?.typography?.fontSize,
		fontStyle: style?.typography?.fontStyle,
		fontWeight: style?.typography?.fontWeight,
		lineHeight: style?.typography?.lineHeight,
		letterSpacing: style?.typography?.letterSpacing,
		textDecoration: style?.typography?.textDecoration,
		textTransform: style?.typography?.textTransform,
		color: style?.color?.text,
		backgroundColor: style?.color?.background,
		textAlign: style?.typography?.textAlign,
		...margins,
		...padding,
		...border,
		...gap,
	};
};

export const getFontFamily = (font) => {
	const fontName = font ?? false;

	const fontFamily = `has-${fontName}-font-family`;

	return fontFamily;
};

export const getVerticalAlignment = (alignment) => {
	let align = false;

	switch (alignment) {
		case 'top':
			align = 'flex-start';
			break;
		case 'center':
		case 'stretch':
			align = alignment;
			break;
		case 'bottom':
			align = 'flex-end';
			break;
	}

	return {
		alignSelf: align,
	};
};

/**
 * Get the alpha from the xex code of masthead in overview.
 */
export function hasAlphaGreaterThanZero(hex = '') {
	// Check if the hex is valid and contains alpha
	if (/^#([0-9A-F]{8})$/i.test(hex)) {
		// Extract the alpha part (last two characters)
		const alpha = hex.slice(7, 9);
		// Convert the alpha part to a number
		const alphaValue = parseInt(alpha, 16);
		// Check if alpha is greater than 0
		return alphaValue > 0;
	} else {
		return true;
	}
}

/**
 * Check that transparency.
 * @param {*} value
 * @returns
 */
export function hasAlphaLessThanOne(hex) {
	// Remove the leading '#' if present
	hex = hex.replace('#', '');

	// If hex length is not 8 (RRGGBBAA), it's not a valid hex with alpha
	if (hex.length !== 8) {
		return false; // No alpha channel or invalid length
	}

	// Extract the AA part (last two characters) of the hex
	const alphaHex = hex.slice(6, 8);

	// Convert the alphaHex (base 16) to a decimal value
	const alphaDecimal = parseInt(alphaHex, 16);

	// Check if the alpha is less than 255 (which is fully opaque)
	return alphaDecimal < 255;
}

export const isNumber = (value) => {
	return typeof value === 'number';
};

/**
 * Get the content position classes.
 * @param {*} positionString A string seperated by spaces denoting content position.
 */
export const getContentPosition = (positionString) => {
	let finalPosition = '';

	const position = positionString.split(' ');

	finalPosition += `is-align-items-${getPosition(position[0])}`;
	finalPosition += ` is-justify-content-${getPosition(position[1])}`;

	return finalPosition;
};

const getPosition = (pos) => {
	if (['top', 'left'].includes(pos)) {
		return 'flex-start';
	}

	if (['bottom', 'right'].includes(pos)) {
		return 'flex-end';
	}

	return 'center';
};

export const getBackgroundPosition = (position) => {
	const x = position.x * 100 + '%';
	const y = position.y * 100 + '%';

	return `${x} ${y}`;
};

/**
 * Returns the component width based on parent component settings.
 */
export const getComponentWidth = (align, width) => {
	if (['left', 'center', 'right', ''].includes(align)) {
		return width.contentSize;
	}

	if ('wide' === align) {
		return width.wideSize;
	}

	return false;
};

/**
 * Check if a param exists in the url.
 * @param {*} parameterName
 * @returns
 */
export const urlHasParameter = (parameterName) => {
	// Get the URL query string
	const queryString = window.location.search;

	// Check if the query string is not empty
	if (queryString.length > 0) {
		// Split the query string into individual parameters
		const params = new URLSearchParams(queryString);

		// Check if the parameter exists
		return params.has(parameterName);
	}

	return false; // Parameter not found
};

/**
 * Get the base domain with the set protocol.
 * @returns
 */
export const getCurrentDomain = (path) => {
	var currentProtocol = window.location.protocol;
	var currentDomain = window.location.hostname;
	return currentProtocol + '//' + currentDomain + '/' + path;
};

/**
 * Stagger Hp animations.
 */
export const staggerAnimations = () => {
	const animatedElements = document.querySelectorAll('.animate');

	animatedElements.forEach((element, index) => {
		element.dataset.hpTriggerDelay = `${index * 200}`;
	});
};
